import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n/config';
import doneIcon from 'assets/images/icons/done-circle-green-filled.svg';
import translateIcon from 'assets/images/icons/translate.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import pdfGreenIcon from 'assets/images/icons/pdf-page.svg';
import midaniLogoArabic from 'assets/images/midani-logo-arabic.png';
import midaniLogoEnglish from 'assets/images/midani-logo-english.png';
import arrowLeft from 'assets/images/icons/arrow-left.svg';
import instructionGreyIcon from 'assets/images/icons/instruction-grey.svg';
import OTPField from 'components/form-components/OTPField';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import TextArea from 'components/form-components/TextArea';
import FormButton from 'components/form-components/FormButton';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import { isRTL } from 'config/config';
import AlertModal from 'components/common-components/AlertModal';
import { useParams } from 'react-router-dom';
import Loader from 'components/common-components/Loader';
import { CallAPI } from 'actions/General';
import toast from 'react-hot-toast';
import MediaPreviewModal from 'pages/report/MediaPreviewModal';
import sectionToggleHook from 'pages/report/sectionToggleHook';
import NestedSelect from 'components/form-components/NestedSelect';
import { changeLanguage } from 'i18next';
import { LanguageReducer } from 'redux/language';

const LanguageActions = LanguageReducer.actions;

const ReviewReport = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const { lang } = useSelector((state) => state.language);
  const [locale, setLocale] = useState(i18n.language);
  const [isLoaded, setIsLoaded] = useState(false);
  const [values, setValues] = useState({
    otp: '',
    review: false,
    rejectText: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submissionWebPageDetail, setSubmissionWebPageDetail] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [displayedHtml, setDisplayedHtml] = useState(null);
  const [reportViewed, setRreportViewed] = useState(false);
  const [step, setStep] = useState(0);
  const hasCalledAPI = useRef([]);

  const languageOptions = [
    { value: 'en', displayValue: 'select_english' },
    { value: 'ar', displayValue: 'select_arabic' },
  ];
  const languageOptionsMap = Object.fromEntries(
    languageOptions.map((l) => [l.value, l.displayValue]),
  );

  const actionList = [
    { value: 'copy_web_link', displayValue: 'action_copy_web_link', isNested: false },
    { value: 'print_report', displayValue: 'action_print', isNested: false },
    { value: 'open_pdf', displayValue: 'action_open_pdf', isNested: false },
  ];

  const handleActions = (_, value) => {
    // eslint-disable-next-line no-undef
    const adminPanelUrl = process.env.REACT_APP_ADMIN_PANEL_URL;
    if (value === 'copy_web_link') {
      const url = `${adminPanelUrl}/report/web_link/real/${submissionWebPageDetail.previewId}`;
      toast('Copied report link to clipboard.', {
        style: { borderRadius: '8px', background: '#000', color: '#fff' },
      });
      navigator.clipboard.writeText(url);
    } else if (value === 'print_report') {
      handlePrintReport(displayedHtml);
    } else if (value === 'open_pdf') {
      const url = `${adminPanelUrl}/report/pdf_preview/real/${submissionWebPageDetail.previewId}`;
      window.open(url, '_blank');
    }
  };

  const handlePrintReport = (htmlData) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlData);
    iframeDoc.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    iframe.onload = () => {
      iframe.remove();
    };
  };

  const handleLogAPI = async (action) => {
    const validActions = ['report_acknowledged', 'report_opened', 'link_opened'];
    if (submissionWebPageDetail.status === 'pending' && validActions.includes(action)) {
      const alreadyLogged =
        submissionWebPageDetail.loggedStatuses.includes(action) ||
        hasCalledAPI.current.includes(action);
      if (!alreadyLogged) {
        const payload = { webPageId: params.id, logAction: action };
        try {
          let result = await CallAPI(
            'ADD_SUBMISSION_WEB_PAGE_LOG',
            payload,
            null,
            setLoading,
            null,
            null,
          );
          if (result.status) {
            hasCalledAPI.current.push(action);
          }
        } catch (error) {
          toast.error('Something went wrong!');
        }
      }
    }
  };

  const getReportHtml = async () => {
    setIsLoaded(false);
    try {
      let result = await CallAPI(
        'GET_PREVIEW_DETAIL',
        { previewId: submissionWebPageDetail.previewId, dataOf: 'real', action: 'web_link' },
        null,
        null,
        null,
        null,
      );
      if (result.status) {
        result = await CallAPI(
          'EXTERNAL_POST_API',
          { ...result.data.data },
          result.data.url,
          null,
          null,
          null,
        );
        setDisplayedHtml(result.data.html);
        setRreportViewed(true);
        handleLogAPI('report_opened');
      }
    } catch (error) {
      toast.error('Something went wrong!');
    }
    setIsLoaded(true);
  };

  const onSubmitOTP = () => {};

  const handleOpenReport = () => {
    setStep(3);
    getReportHtml();
  };

  const handleSendResponse = async (type) => {
    const payload = {
      webPageId: params.id,
      response: type,
      rejectReason: type === 'rejected' ? values.rejectText : null,
    };
    try {
      let result = await CallAPI(
        'SUBMIT_SUBMISSION_WEB_PAGE_RESPONSE',
        payload,
        null,
        setLoading,
        null,
        null,
      );
      if (result.status) {
        setStep(5);
      }
    } catch (error) {
      toast.error('Something went wrong!');
    }
  };

  const handleChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setValues({ ...values, [field]: value });
    if (field === 'review') {
      handleUserInteractions('report_acknowledged');
    }
  };

  const handleUserInteractions = (action, type) => {
    if (action === 'report_opened') {
      handleOpenReport(action);
    } else if (action === 'report_acknowledged') {
      handleLogAPI(action);
    } else if (action === 'submit') {
      if (type === 'approved') {
        handleSendResponse(type);
      } else {
        setShowRejectModal(true);
      }
    }
  };

  const getDetail = async () => {
    try {
      let result = await CallAPI(
        'GET_SUBMISSION_WEB_PAGE_DETAIL',
        null,
        params.id,
        null,
        null,
        null,
      );
      if (result.status) {
        if (result.data.loggedStatuses.includes('report_opened')) {
          setRreportViewed(true);
        }
        setSubmissionWebPageDetail({
          title: result.data.form.title,
          logType: result.data.logType,
          loggedStatuses: result.data.loggedStatuses,
          previewId: result.data.previewId,
          recipient: result.data.recipient,
          responseEnabled: result.data.responseEnabled,
          status: result.data.status,
          referenceNumber: result.data.submissionId.referenceNumber,
          customTemplate: result.data.customTemplate ?? null,
        });
      }
    } catch (error) {
      toast.error('Something went wrong!');
    }
    setIsLoaded(true);
  };

  const getResponseMessages = (webPageDetail) => {
    if (webPageDetail.responseEnabled) {
      const customTemplate = webPageDetail.customTemplate;
      if (!customTemplate) {
        return [
          t('text_webpage_description_response_enabled_text_1'),
          '',
          '',
          t('text_webpage_description_response_enabled_text_2'),
        ];
      } else {
        return isRTL() ? customTemplate.arabicMessageLines : customTemplate.englishMessageLines;
      }
    } else {
      return [
        t('text_webpage_description_response_disabled_text_1'),
        '',
        '',
        t('text_webpage_description_response_disabled_text_2'),
      ];
    }
  };

  const handleLanguageChange = (_, value) => {
    dispatch(LanguageActions.switchLanguage(value));
  };

  useEffect(() => {
    if (!isLoaded) getDetail();
  }, []);

  useEffect(() => {
    if (submissionWebPageDetail) {
      handleLogAPI('link_opened');
      setStep(2);
    }
  }, [submissionWebPageDetail]);

  useEffect(() => {
    changeLanguage(lang);
    setLocale(lang);
    document.dir = i18n.dir();
  }, [lang]);

  return (
    <>
      {!isLoaded && <Loader color='green-1' type='modal-spinner' />}
      {isLoaded && (
        <div className='sr-layout'>
          <div className='sr-header'>
            <label className='cursor-pointer' onClick={() => setStep(2)}>
              {t('text_report')}
            </label>
            <div className='d-flex gap-3 align-items-center'>
              {step === 3 && displayedHtml && (
                <NestedSelect
                  options={actionList}
                  handleChange={handleActions}
                  name='share'
                  toggleClasses='px-1'
                  toggleComponent={
                    <>
                      <label className='cursor-pointer'>{t('button_share')}</label>
                      <img src={chevronGreyIcon} className='rotate-180 svg-black' />
                    </>
                  }
                />
              )}
              <div className='d-flex gap-1 align-items-center'>
                <img src={translateIcon} alt='translate' className='mt-1 theme-height-20' />
                <NestedSelect
                  options={languageOptions}
                  handleChange={handleLanguageChange}
                  name='locale'
                  toggleClasses='px-1'
                  toggleComponent={
                    <>
                      <label className='cursor-pointer'>
                        {t(languageOptionsMap[locale] ?? 'select_language')}
                      </label>
                      <img src={chevronGreyIcon} className='rotate-180 svg-black' />
                    </>
                  }
                />
              </div>
              <img
                className='logo'
                src={isRTL() ? midaniLogoArabic : midaniLogoEnglish}
                alt='logo'
              />
            </div>
          </div>
          {step === 1 && (
            <div className='sr-cards-container mt-5'>
              <div className='sr-card align-items-center'>
                <img
                  className='appLogo'
                  src={isRTL() ? midaniLogoArabic : midaniLogoEnglish}
                  alt='logo'
                />
                <span className='theme-size-1_3 theme-text-black-1'>
                  {t('text_enter_otp_code_to_access_report')}
                </span>
                <span>{t('text_enter_the_code_we_sent_via_sms_to')} </span>
                <span>{submissionWebPageDetail.recipient}</span>

                <OTPField
                  name='otp'
                  handleChange={handleChange}
                  error={errors.otp}
                  touched={touched.otp}
                />

                <FormLoadingButton
                  text='button_verify'
                  variant='green-1'
                  loading={loading}
                  onClick={onSubmitOTP}
                  disabled={!(values.otp.length === 4)}
                  classes='mt-3'
                />
              </div>
            </div>
          )}

          {(step === 2 || step === 4) && (
            <div className='sr-cards-container mt-5'>
              <div className='sr-card align-items-center'>
                <span className='align-self-start theme-size-1_2 theme-font-inter-semi-bold theme-text-black-1'>
                  {submissionWebPageDetail.title}
                </span>
                <span className='align-self-start'>
                  {`${t('text_reference')}# ${submissionWebPageDetail.referenceNumber}`}{' '}
                </span>
                <div className='w-100'></div>
                <div
                  className='mt-3 sr-description'
                  style={{
                    direction: isRTL() ? 'rtl' : 'ltr',
                  }}
                >
                  {getResponseMessages(submissionWebPageDetail).map((line, index) => (
                    <span key={index}>{line ? line : <br />}</span>
                  ))}
                </div>
                <FormButton
                  text='button_open_report'
                  variant={'green-2'}
                  icon={pdfGreenIcon}
                  onClick={() => handleUserInteractions('report_opened')}
                  classes='mt-3 w-100'
                />
              </div>

              {submissionWebPageDetail.status === 'pending' &&
                submissionWebPageDetail.responseEnabled && (
                  <div>
                    <SingleCheckBoxField
                      name='review'
                      value={values.review}
                      handleChange={handleChange}
                      text={
                        <span className='theme-text-black-1'>
                          {t('text_i_have_reviewed_the_report')}
                        </span>
                      }
                      disabled={!reportViewed || loading}
                      classes='mt-3'
                    />

                    <div className='sr-card mt-3 theme-size-1_2'>
                      <span className='theme-text-black-1'>{t('text_response')}</span>
                      <div className='mt-2 d-flex gap-2'>
                        <FormButton
                          text='button_reject'
                          variant='red-1'
                          onClick={() => handleUserInteractions('submit', 'rejected')}
                          disabled={!values.review}
                          classes='w-100'
                        />
                        <FormLoadingButton
                          text='button_approve'
                          variant='green-2'
                          onClick={() => handleUserInteractions('submit', 'approved')}
                          loading={loading}
                          disabled={!values.review || loading}
                          classes='w-100'
                        />
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}

          {step === 3 && (
            <div className='sr-report'>
              <div className='d-flex align-items-center gap-3 my-3'>
                <img
                  className='icon back-arrow cursor-pointer'
                  src={arrowLeft}
                  onClick={() => setStep(4)}
                />
                <label className='theme-size-1_2 theme-font-inter-semi-bold'>
                  {t('text_back')}
                </label>
              </div>
              <div className='iframe-container'>
                {!isLoaded && <Loader color='green-1' type='modal-spinner' />}
                {displayedHtml && <ReportComponent displayedHtml={displayedHtml} />}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className='sr-cards-container sr-final'>
              <img src={doneIcon} />
              <span className='theme-size-1_2 theme-text-black-1 theme-font-inter-semi-bold'>
                {t('text_response_submitted')}
              </span>
              <span>{t('text_thank_you')}</span>
            </div>
          )}

          {showRejectModal && (
            <AlertModal
              show={showRejectModal}
              closeModal={() => setShowRejectModal(false)}
              actionButtons={[]}
            >
              <div className='w-100 theme-font-inter-light'>
                <div className='d-flex gap-2 py-3'>
                  <img src={instructionGreyIcon} />
                  <label className='theme-size-1_2 theme-font-inter-bold theme-text-black-2'>
                    {t('text_prompt_reject_report')}
                  </label>
                </div>
                <TextArea
                  name='rejectText'
                  displayName='field_reason'
                  placeholder='text_type_here'
                  value={values.rejectText}
                  error={errors.rejectText}
                  touched={touched.rejectText}
                  handleChange={handleChange}
                  rows={6}
                  classes='w-100 mb-2'
                />

                <div className='d-flex justify-content-end gap-2 p-3'>
                  <FormButton
                    text={'button_cancel'}
                    variant='white-1'
                    onClick={() => setShowRejectModal(false)}
                  />
                  <FormLoadingButton
                    text={'button_reject'}
                    variant='red-1'
                    disabled={values.rejectText.length === 0 || loading}
                    loading={loading}
                    onClick={() => {
                      setShowRejectModal(false);
                      handleSendResponse('rejected');
                    }}
                  />
                </div>
              </div>
            </AlertModal>
          )}
        </div>
      )}
    </>
  );
};

const ReportComponent = ({ displayedHtml }) => {
  // Custom hooks
  sectionToggleHook(displayedHtml);

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: displayedHtml }}></div>
      <MediaPreviewModal displayedHtml={displayedHtml} />
    </>
  );
};

ReportComponent.propTypes = {
  displayedHtml: PropTypes.string.isRequired,
};

export default ReviewReport;
